<template>
  <div class="analytics-card" :class="{ 'loading': isLoading, 'expanded': expanded }">
    <div class="card-header" @click="expanded = !expanded">
      <div class="header-left">
        <div class="icon-container" :style="{ background: iconBackground }">
          <div class="card-icon">{{ icon }}</div>
        </div>
        <div class="header-content">
          <h3 class="card-title">{{ title }}</h3>
          <p class="card-subtitle" v-if="subtitle">{{ subtitle }}</p>
        </div>
      </div>
      <div class="header-right">
        <span v-if="badge" class="badge" :class="badgeType">{{ badge }}</span>
        <button class="expand-button" v-if="hasExpandableContent">
          <span v-if="expanded">↑</span>
          <span v-else>↓</span>
        </button>
      </div>
    </div>
    
    <div class="card-body">
      <div class="value-container">
        <span class="card-value">{{ prefix }}{{ formattedValue }}</span>
        <div v-if="trend !== null" class="trend-indicator" :class="trendClass">
          <span class="trend-arrow">{{ trend > 0 ? '↑' : '↓' }}</span>
          <span class="trend-value">{{ Math.abs(trend).toFixed(1) }}%</span>
        </div>
      </div>
      
      <p v-if="description" class="card-description">{{ description }}</p>
      
      <div v-if="compareValue !== null" class="comparison">
        <div class="compare-label">{{ compareLabel }}</div>
        <div class="compare-value">{{ prefix }}{{ formattedCompareValue }}</div>
        <div class="compare-diff" :class="compareDiffClass">
          {{ compareDiffPrefix }}{{ Math.abs(compareDiff).toFixed(1) }}%
        </div>
      </div>
    </div>
    
    <div v-if="expanded && hasExpandableContent" class="card-expanded">
      <slot name="expanded"></slot>
    </div>
    
    <div v-if="isLoading" class="loading-shimmer"></div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'AnalyticsCard',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    value: {
      type: [Number, String],
      required: true
    },
    prefix: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: '📊'
    },
    iconBackground: {
      type: String,
      default: '#ebf5ff'
    },
    description: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    trend: {
      type: Number,
      default: null
    },
    badge: {
      type: String,
      default: ''
    },
    badgeType: {
      type: String,
      default: 'neutral',
      validator: (value) => ['success', 'warning', 'danger', 'info', 'neutral'].includes(value)
    },
    compareValue: {
      type: [Number, String],
      default: null
    },
    compareLabel: {
      type: String,
      default: 'vs Previous Period'
    },
    formatter: {
      type: Function,
      default: null
    }
  },
  setup(props, { slots }) {
    const expanded = ref(false);
    
    // Check if there's expandable content
    const hasExpandableContent = computed(() => {
      return !!slots.expanded;
    });
    
    // Format the values for display
    const formattedValue = computed(() => {
      return formatValue(props.value);
    });
    
    const formattedCompareValue = computed(() => {
      if (props.compareValue === null) return null;
      return formatValue(props.compareValue);
    });
    
    // Helper to format values
    const formatValue = (value) => {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value;
      
      // Use custom formatter if provided
      if (props.formatter) {
        return props.formatter(numValue) + props.suffix;
      }
      
      // Default formatting
      if (Math.abs(numValue) >= 1000000) {
        return (numValue / 1000000).toFixed(1) + 'M' + props.suffix;
      } else if (Math.abs(numValue) >= 1000) {
        return (numValue / 1000).toFixed(1) + 'K' + props.suffix;
      } else if (numValue % 1 !== 0) {
        return numValue.toFixed(1) + props.suffix;
      }
      
      return numValue.toLocaleString() + props.suffix;
    };
    
    // Trend indicator classes
    const trendClass = computed(() => {
      if (props.trend === null) return '';
      return props.trend > 0 ? 'positive' : 'negative';
    });
    
    // Compare values difference
    const compareDiff = computed(() => {
      if (props.compareValue === null || props.value === null) return 0;
      
      const current = parseFloat(props.value) || 0;
      const previous = parseFloat(props.compareValue) || 0;
      
      if (previous === 0) return 0;
      
      return ((current - previous) / previous) * 100;
    });
    
    const compareDiffClass = computed(() => {
      if (compareDiff.value === 0) return 'neutral';
      return compareDiff.value > 0 ? 'positive' : 'negative';
    });
    
    const compareDiffPrefix = computed(() => {
      if (compareDiff.value === 0) return '';
      return compareDiff.value > 0 ? '+' : '';
    });
    
    return {
      expanded,
      hasExpandableContent,
      formattedValue,
      formattedCompareValue,
      trendClass,
      compareDiff,
      compareDiffClass,
      compareDiffPrefix
    };
  }
};
</script>

<style scoped>
.analytics-card {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
}

.analytics-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon-container {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon {
  font-size: 1.5rem;
}

.card-title {
  margin: 0;
  font-size: 1rem;
  color: #334155;
  font-weight: 600;
}

.card-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.value-container {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.card-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.trend-indicator {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.trend-indicator.positive {
  background-color: #dcfce7;
  color: #166534;
}

.trend-indicator.negative {
  background-color: #fee2e2;
  color: #b91c1c;
}

.trend-arrow {
  margin-right: 0.25rem;
}

.card-description {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.comparison {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
  font-size: 0.875rem;
}

.compare-label {
  color: #64748b;
}

.compare-value {
  font-weight: 600;
  color: #334155;
}

.compare-diff {
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.compare-diff.positive {
  background-color: #dcfce7;
  color: #166534;
}

.compare-diff.negative {
  background-color: #fee2e2;
  color: #b91c1c;
}

.compare-diff.neutral {
  background-color: #f1f5f9;
  color: #64748b;
}

.badge {
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge.success {
  background-color: #dcfce7;
  color: #166534;
}

.badge.warning {
  background-color: #fef9c3;
  color: #854d0e;
}

.badge.danger {
  background-color: #fee2e2;
  color: #b91c1c;
}

.badge.info {
  background-color: #dbeafe;
  color: #1e40af;
}

.badge.neutral {
  background-color: #f1f5f9;
  color: #475569;
}

.expand-button {
  width: 24px;
  height: 24px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.expand-button:hover {
  background: #e2e8f0;
  color: #334155;
}

.card-expanded {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.analytics-card.expanded {
  margin-bottom: 1.5rem;
}

/* Loading state */
.analytics-card.loading {
  pointer-events: none;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

.analytics-card.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.4);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .header-right {
    align-self: flex-end;
  }
  
  .value-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .comparison {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style> 